import { loadScript, waitFor } from './loadScript';
import { isHeicName, type PickedFile } from './types';

interface OneDriveItem {
  name: string;
  size?: number;
  '@microsoft.graph.downloadUrl'?: string;
}

interface OneDriveResult {
  value: OneDriveItem[];
}

interface OneDriveSdk {
  open(options: {
    clientId: string;
    action: 'download' | 'share' | 'query';
    multiSelect: boolean;
    openInNewWindow?: boolean;
    advanced?: { redirectUri?: string; filter?: string; queryParameters?: string };
    success(result: OneDriveResult): void;
    cancel?(): void;
    error?(error: unknown): void;
  }): void;
}

declare global {
  interface Window {
    OneDrive?: OneDriveSdk;
  }
}

const clientId = import.meta.env.VITE_ONEDRIVE_CLIENT_ID as string | undefined;

export const oneDriveConfigured = Boolean(clientId);

/**
 * OneDrive File Picker v7.2. `action: 'download'` gives back a pre-authorised
 * download URL per item, so no token has to leave the browser.
 */
export async function pickFromOneDrive(): Promise<PickedFile[]> {
  if (!clientId) throw new Error('OneDrive is not configured on this deployment');

  await loadScript('https://js.live.net/v7.2/OneDrive.js');
  const sdk = await waitFor(() => window.OneDrive);

  return new Promise((resolve, reject) => {
    sdk.open({
      clientId,
      action: 'download',
      multiSelect: true,
      advanced: {
        // Must exactly match a redirect URI registered on the Azure app.
        redirectUri: window.location.origin,
        filter: '.heic,.heif',
      },
      success: (result) => {
        const items = result?.value ?? [];
        resolve(
          items
            .filter((item) => item['@microsoft.graph.downloadUrl'] && isHeicName(item.name))
            .map((item) => ({
              name: item.name,
              url: item['@microsoft.graph.downloadUrl'] as string,
              bytes: item.size,
              source: 'onedrive' as const,
            })),
        );
      },
      cancel: () => resolve([]),
      error: (error) => reject(error instanceof Error ? error : new Error('The OneDrive picker reported an error')),
    });
  });
}
